// Where moved work went, and its Undo (#6772, epic #6552; prototype moveNoteHTML `:1779-1785`,
// undoMove `:1674-1681`). Pure: `lib/accepted-move.ts` holds the newest batch this page
// accepted and the reads of its tasks, and `components/machine-card/MoveNote.tsx` renders this.
//
// THE NOTE stays on the newest batch only (a newer one replaces it), in Now for a job move and
// in Mission for an epic move: on the TARGET card when that card is rendered ("moved here
// from"), else on the SOURCE ("moved to"). It goes once its Undo has been live for 7 s, or 7 s
// after a read first showed any of its items `failed`. An Undo's own batch shows no note: the
// announcement says the work moved back.
//
// UNDO FOLLOWS THE SERVER (a coordinator choice, #6772). `undoVerdict` (api `deps/box-move.ts`)
// accepts only the newest batch once every item has landed, and each landed item refuses to
// move again until its `rest_until` (`cooling_down`) and while it starts (`still_starting`).
// So: any item still `checkpointing` / `moving` (or not read yet) reads "Undo once it lands",
// with no countdown (`secondsUntil` never reads below 1); every item landed, "Undo in N s"
// until the batch's latest `rest_until`; then live for 7 s. An item `failed` settles the batch
// `partial` or `failed`, which no Undo can reverse: none is offered. A batch job reading
// `starting` on the target card keeps it disabled, approximating `still_starting`.

import type { Translator } from '@developerz/i18n';
import type { TaskRow } from './fleet';
import { secondsUntil } from './time';

/** How long Undo stays live once the batch has rested, and a failed batch's note stays. */
export const UNDO_LIVE_MS = 7_000;

/** A task's newest move, as the task resource reads it. */
export type TaskMove = NonNullable<TaskRow['move']>;

/** One batch this page accepted (the move's 201), with the words its note reads. */
export type MoveBatch = {
  readonly id: string;
  readonly fromId: string;
  readonly toId: string;
  /** The 201's `batch.items[].task_id`. */
  readonly taskIds: readonly string[];
  /** "The Coder job", "Epic <title> and its N jobs", "N epic(s) and N job(s)". */
  readonly what: string;
  /** Now for job moves, Mission for epic moves. */
  readonly zone: 'now' | 'mission';
  /** This batch IS an Undo: it shows no note and offers no Undo of its own. */
  readonly undo: boolean;
};

export type UndoView =
  /** An item is still checkpointing or moving, or not read yet. */
  | { readonly kind: 'landing' }
  /** Every item landed; the batch rests until `untilMs`. */
  | { readonly kind: 'resting'; readonly untilMs: number }
  /** A batch job still starts on the target card. */
  | { readonly kind: 'starting' }
  /** The server would accept it now, until `untilMs`. */
  | { readonly kind: 'live'; readonly untilMs: number }
  /** Its live window passed: the note goes. */
  | { readonly kind: 'expired' }
  /** Never: an item failed, or moved again in another batch. */
  | { readonly kind: 'none' };

/** Undo's state for `batch` at `nowMs`, from the tasks' own `move` reads. */
export function undoView(
  batch: MoveBatch,
  moveOf: (taskId: string) => TaskMove | null | undefined,
  starting: (taskId: string) => boolean,
  nowMs: number,
): UndoView {
  const moves = batch.taskIds.map(moveOf);
  // Moved again since in another batch, or failed: this batch can never be undone.
  if (moves.some((m) => m != null && (m.batch_id !== batch.id || m.state === 'failed'))) {
    return { kind: 'none' };
  }
  if (moves.some((m) => m == null || m.state !== 'landed')) return { kind: 'landing' };
  let rested = 0;
  for (const m of moves) {
    const at = m == null ? Number.NaN : Date.parse(m.rest_until);
    if (!Number.isNaN(at)) rested = Math.max(rested, at);
  }
  if (nowMs < rested) return { kind: 'resting', untilMs: rested };
  if (nowMs >= rested + UNDO_LIVE_MS) return { kind: 'expired' };
  if (batch.taskIds.some(starting)) return { kind: 'starting' };
  return { kind: 'live', untilMs: rested + UNDO_LIVE_MS };
}

/** Undo's words: "Undo once it lands", "Undo in N s", or "Undo". */
export function undoText(v: UndoView, nowMs: number, tr: Translator): string {
  if (v.kind === 'landing' || v.kind === 'starting') return tr('fleet.move.undo_landing');
  if (v.kind === 'resting')
    return tr('fleet.move.undo_in', { seconds: secondsUntil(v.untilMs, nowMs) });
  return tr('fleet.move.undo');
}

/** Undo is offered (shown) for this view: not for a failed or expired batch. */
export const undoShown = (v: UndoView): boolean => v.kind !== 'none' && v.kind !== 'expired';

/** What the note reads on. */
export type MoveNoteInput = {
  readonly batch: MoveBatch;
  /** The newest batch this page accepted. */
  readonly newestId: string;
  /** The machines whose cards are rendered now. */
  readonly rendered: ReadonlySet<string>;
  readonly moveOf: (taskId: string) => TaskMove | null | undefined;
  readonly starting: (taskId: string) => boolean;
  /** When a read first showed an item failed, `Date.now` ms; null while none has. */
  readonly failedAt: number | null;
  readonly now: number;
  readonly nameOf: (boxId: string) => string;
};

export type MoveNoteView = {
  /** The card it shows on. */
  readonly boxId: string;
  /** The batch's target: an Undo is sent off it, and the page's pre-send check reads it. */
  readonly undoFrom: string;
  /** The batch's source: an Undo sends the work back there. */
  readonly undoTo: string;
  readonly zone: MoveBatch['zone'];
  readonly text: string;
  readonly undo: UndoView;
};

/** Each item of a batch, as its fresh read has it: landed, failed, or still in transit. */
function tally(b: MoveBatch, moveOf: MoveNoteInput['moveOf']) {
  let landed = 0;
  let failed = 0;
  for (const id of b.taskIds) {
    const m = moveOf(id);
    if (m == null || m.batch_id !== b.id) continue;
    if (m.state === 'landed') landed += 1;
    else if (m.state === 'failed') failed += 1;
  }
  return { landed, failed, total: b.taskIds.length };
}

/**
 * What the note SAYS, and on which card, decided from the items' states alone (#6772 review
 * rounds 10 and 11), never from the 201. An item no fresh read shows settled is in transit, and
 * only LANDED items count as moved:
 * - every item failed: "<what> didn't move to <name>", on the source card;
 * - any still in transit: "<what>: moving here / to <name>", or "<n> of <total> moved ... so
 *   far" once some landed;
 * - all settled, some failed: "<n> of <total> moved ..., the rest stayed";
 * - all landed: "<what> moved here / to <name>".
 */
function noteWording(
  b: MoveBatch,
  moveOf: MoveNoteInput['moveOf'],
  onTarget: boolean,
  nameOf: (boxId: string) => string,
  tr: Translator,
): { readonly boxId: string; readonly text: string } {
  const { landed, failed, total } = tally(b, moveOf);
  if (failed === total) {
    return {
      boxId: b.fromId,
      text: tr('fleet.move.note_failed', { what: b.what, name: nameOf(b.toId) }),
    };
  }
  const boxId = onTarget ? b.toId : b.fromId;
  const name = nameOf(onTarget ? b.fromId : b.toId);
  const counts = { moved: landed, total, name };
  if (landed + failed < total) {
    const text =
      landed === 0
        ? onTarget
          ? tr('fleet.move.note_moving_here', { what: b.what, name })
          : tr('fleet.move.note_moving_to', { what: b.what, name })
        : onTarget
          ? tr('fleet.move.note_so_far_here', counts)
          : tr('fleet.move.note_so_far_to', counts);
    return { boxId, text };
  }
  if (failed > 0) {
    const text = onTarget
      ? tr('fleet.move.note_partial_here', counts)
      : tr('fleet.move.note_partial_to', counts);
    return { boxId, text };
  }
  const text = onTarget
    ? tr('fleet.move.note_here', { what: b.what, name })
    : tr('fleet.move.note_to', { what: b.what, name });
  return { boxId, text };
}

/** The note for the newest batch, or null when there is none to show. */
export function moveNoteView(n: MoveNoteInput, tr: Translator): MoveNoteView | null {
  const b = n.batch;
  if (b.id !== n.newestId || b.undo) return null;
  if (n.failedAt !== null && n.now >= n.failedAt + UNDO_LIVE_MS) return null;
  const undo = undoView(b, n.moveOf, n.starting, n.now);
  if (undo.kind === 'expired') return null;
  const base = { undoFrom: b.toId, undoTo: b.fromId, zone: b.zone, undo };
  const w = noteWording(b, n.moveOf, n.rendered.has(b.toId), n.nameOf, tr);
  return { ...base, boxId: w.boxId, text: w.text };
}
